/** Bugs & Requests.
 *
 * The pilot's version earned its place by being one click away from wherever the problem
 * was, so the report happened while the person still remembered the details. Two things
 * carry that over: the page the reporter was on is captured automatically, and the queue
 * is visible to everyone who can file — a box people cannot see into gets used once.
 */

import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Bug, ImagePlus, Lightbulb, HelpCircle, Pencil } from 'lucide-react'

import {
  Button,
  Can,
  EmptyState,
  ErrorState,
  Field,
  Loading,
  Pill,
  SectionHeader,
  inputClass,
} from '@/components/ui'
import { Permissions, usePermission, useSession } from '@/lib/session'
import { AttachmentThumb, ImagePicker, PendingThumb } from '@/features/feedback/Attachments'
import { imagesFromPaste, vetImages } from '@/features/feedback/attachmentFiles'
import {
  useEditFeedbackReport,
  useFeedback,
  useRemoveFeedbackAttachment,
  useSubmitFeedback,
  useUploadFeedbackAttachment,
  useTriageFeedback,
  type FeedbackItem,
} from '@/features/feedback/useFeedback'

const KIND_ICON = { bug: Bug, request: Lightbulb, question: HelpCircle } as const

// Said out loud beside the icon's tooltip. An unlabelled light bulb read as an upvote
// button that did nothing.
const KIND_LABEL: Record<FeedbackItem['kind'], string> = {
  bug: 'Bug',
  request: 'Request',
  question: 'Question',
}

const SEVERITY_TONE = {
  blocking: 'red',
  high: 'red',
  normal: 'info',
  low: 'neutral',
} as const

const STATUS_TONE = {
  new: 'amber',
  planned: 'info',
  in_progress: 'info',
  resolved: 'green',
  declined: 'neutral',
} as const

const STATUS_LABEL: Record<FeedbackItem['status'], string> = {
  new: 'New',
  planned: 'Planned',
  in_progress: 'In progress',
  resolved: 'Resolved',
  declined: 'Declined',
}

function SubmitForm({ page }: { page: string }) {
  const submit = useSubmitFeedback()
  const upload = useUploadFeedbackAttachment()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [kind, setKind] = useState<FeedbackItem['kind']>('bug')
  const [severity, setSeverity] = useState<FeedbackItem['severity']>('normal')
  /** Images chosen or pasted, uploaded once the item exists. */
  const [pending, setPending] = useState<File[]>([])
  const [problems, setProblems] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)

  const addFiles = (files: File[]) => {
    const { accepted, problems: found } = vetImages(files, pending.length)
    setPending((current) => [...current, ...accepted])
    setProblems(found)
  }

  /**
   * File the item, then upload its images one by one. The item goes first because an
   * image has to belong to something; if an upload fails the item is already filed and
   * says so, and the reporter can add the image again from the list.
   */
  const send = () => {
    if (!title.trim()) return
    setProblems([])
    submit.mutate(
      { title: title.trim(), description: description.trim(), kind, severity, page },
      {
        onSuccess: (item) => void attachAll(item.id),
      },
    )
  }

  const attachAll = async (itemId: string) => {
    const failed: string[] = []
    setUploading(true)
    for (const file of pending) {
      try {
        await upload.mutateAsync({ itemId, file })
      } catch (error) {
        failed.push(
          `${file.name || 'An image'}: ${error instanceof Error ? error.message : 'upload failed'}`,
        )
      }
    }
    setUploading(false)
    setTitle('')
    setDescription('')
    setSeverity('normal')
    setPending([])
    setProblems(
      failed.length
        ? ['Filed, but these images did not attach — add them again from the list:', ...failed]
        : [],
    )
  }

  return (
    <div
      className="card p-4"
      // A paste anywhere in the form: screenshots arrive on the clipboard far more often
      // than as files. Text pastes carry no files and pass straight through.
      onPaste={(event) => {
        const images = imagesFromPaste(event)
        if (images.length === 0) return
        event.preventDefault()
        addFiles(images)
      }}
    >
      <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
        <Field label="What happened?">
          <input
            className={inputClass}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="The roadmap shows the wrong month for…"
          />
        </Field>
        <Field label="Type">
          <select
            className={inputClass}
            value={kind}
            onChange={(event) => setKind(event.target.value as FeedbackItem['kind'])}
          >
            <option value="bug">Bug</option>
            <option value="request">Request</option>
            <option value="question">Question</option>
          </select>
        </Field>
        <Field label="Severity">
          <select
            className={inputClass}
            value={severity}
            onChange={(event) => setSeverity(event.target.value as FeedbackItem['severity'])}
          >
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="blocking">Blocking</option>
          </select>
        </Field>
      </div>
      <div className="mt-3">
        <Field label="Any detail that would help" hint={`Filed against ${page}`}>
          <textarea
            className={`${inputClass} min-h-[72px]`}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="What you expected, what happened instead, and anything that reproduces it."
          />
        </Field>
      </div>
      <div className="mt-3 space-y-2">
        <ImagePicker onFiles={addFiles} held={pending.length} disabled={submit.isPending || uploading} />
        {pending.length ? (
          <ul className="flex flex-wrap gap-3 pt-1">
            {pending.map((file, index) => (
              <PendingThumb
                key={`${file.name}-${file.size}-${index}`}
                file={file}
                onRemove={() => setPending((current) => current.filter((_, i) => i !== index))}
              />
            ))}
          </ul>
        ) : null}
        {problems.length ? (
          <ul className="text-xs" style={{ color: 'var(--status-red)' }}>
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        ) : null}
      </div>
      <div className="mt-3 flex items-center gap-3">
        <Button
          variant="primary"
          onClick={send}
          disabled={!title.trim() || submit.isPending || uploading}
        >
          {submit.isPending ? 'Sending…' : uploading ? 'Attaching images…' : 'File it'}
        </Button>
        {submit.isError ? <ErrorState error={submit.error} /> : null}
        {submit.isSuccess && !uploading ? (
          <span className="text-sm" style={{ color: 'var(--status-green)' }}>
            Filed. Thank you.
          </span>
        ) : null}
      </div>
    </div>
  )
}

function TriageControls({ item }: { item: FeedbackItem }) {
  const triage = useTriageFeedback()
  const [resolution, setResolution] = useState(item.resolution)

  return (
    <div className="mt-3 flex flex-wrap items-end gap-2 border-t border-border pt-3">
      <Field label="Status">
        <select
          className={inputClass}
          value={item.status}
          onChange={(event) =>
            triage.mutate({
              id: item.id,
              status: event.target.value as FeedbackItem['status'],
              row_version: item.rowVersion,
            })
          }
        >
          {(Object.keys(STATUS_LABEL) as FeedbackItem['status'][]).map((value) => (
            <option key={value} value={value}>
              {STATUS_LABEL[value]}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Severity">
        <select
          className={inputClass}
          value={item.severity}
          onChange={(event) =>
            triage.mutate({
              id: item.id,
              severity: event.target.value as FeedbackItem['severity'],
              row_version: item.rowVersion,
            })
          }
        >
          <option value="low">Low</option>
          <option value="normal">Normal</option>
          <option value="high">High</option>
          <option value="blocking">Blocking</option>
        </select>
      </Field>
      <div className="min-w-[220px] flex-1">
        <Field label="Resolution">
          <input
            className={inputClass}
            value={resolution}
            onChange={(event) => setResolution(event.target.value)}
            onBlur={() => {
              if (resolution !== item.resolution) {
                triage.mutate({ id: item.id, resolution, row_version: item.rowVersion })
              }
            }}
            placeholder="What was done, or why not"
          />
        </Field>
      </div>
      {triage.isError ? (
        <p className="text-xs" style={{ color: 'var(--status-red)' }}>
          {triage.error instanceof Error ? triage.error.message : 'Could not update.'}
        </p>
      ) : null}
    </div>
  )
}

/** The reporter's own title and description, edited in place. */
function EditReport({ item, onDone }: { item: FeedbackItem; onDone: () => void }) {
  const edit = useEditFeedbackReport()
  const [title, setTitle] = useState(item.title)
  const [description, setDescription] = useState(item.description)

  return (
    <div className="mt-3 space-y-3 border-t border-border pt-3">
      <Field label="Title">
        <input
          className={inputClass}
          value={title}
          autoFocus
          onChange={(event) => setTitle(event.target.value)}
        />
      </Field>
      <Field label="Detail">
        <textarea
          className={`${inputClass} min-h-[72px]`}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </Field>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          disabled={!title.trim() || edit.isPending}
          onClick={() =>
            edit.mutate(
              {
                id: item.id,
                title: title.trim(),
                description: description.trim(),
                row_version: item.rowVersion,
              },
              { onSuccess: onDone },
            )
          }
        >
          {edit.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button onClick={onDone}>Cancel</Button>
        {edit.isError ? <ErrorState error={edit.error} /> : null}
      </div>
    </div>
  )
}

/**
 * The images on an item, and — for whoever may change them — remove buttons and a picker.
 * Uploads start the moment a file is chosen or pasted; there is nothing to save.
 */
function AttachmentStrip({
  item,
  canAttach,
  adding,
  setAdding,
}: {
  item: FeedbackItem
  canAttach: boolean
  adding: boolean
  setAdding: (adding: boolean) => void
}) {
  const upload = useUploadFeedbackAttachment()
  const remove = useRemoveFeedbackAttachment()
  const [problems, setProblems] = useState<string[]>([])

  const addFiles = async (files: File[]) => {
    const { accepted, problems: found } = vetImages(files, item.attachments.length)
    const failed = [...found]
    for (const file of accepted) {
      try {
        await upload.mutateAsync({ itemId: item.id, file })
      } catch (error) {
        failed.push(
          `${file.name || 'An image'}: ${error instanceof Error ? error.message : 'upload failed'}`,
        )
      }
    }
    setProblems(failed)
    if (failed.length === 0) setAdding(false)
  }

  if (item.attachments.length === 0 && !adding) return null

  return (
    <div
      className="mt-3"
      onPaste={(event) => {
        if (!adding) return
        const images = imagesFromPaste(event)
        if (images.length === 0) return
        event.preventDefault()
        void addFiles(images)
      }}
    >
      {item.attachments.length ? (
        <ul className="flex flex-wrap gap-3">
          {item.attachments.map((attachment) => (
            <AttachmentThumb
              key={attachment.id}
              itemId={item.id}
              attachment={attachment}
              removing={remove.isPending}
              onRemove={
                canAttach
                  ? () => {
                      if (window.confirm(`Remove ${attachment.fileName}? This deletes the image.`)) {
                        remove.mutate({ itemId: item.id, attachmentId: attachment.id })
                      }
                    }
                  : undefined
              }
            />
          ))}
        </ul>
      ) : null}
      {adding ? (
        <div className="mt-2 space-y-1">
          <ImagePicker
            onFiles={(files) => void addFiles(files)}
            held={item.attachments.length}
            disabled={upload.isPending}
          />
          <div className="flex items-center gap-3 text-xs">
            {upload.isPending ? <span className="text-fg-muted">Uploading…</span> : null}
            <button type="button" className="text-fg-muted underline" onClick={() => setAdding(false)}>
              Done
            </button>
          </div>
        </div>
      ) : null}
      {problems.length ? (
        <ul className="mt-1 text-xs" style={{ color: 'var(--status-red)' }}>
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      ) : null}
      {remove.isError ? (
        <p className="mt-1 text-xs" style={{ color: 'var(--status-red)' }}>
          {remove.error instanceof Error ? remove.error.message : 'Could not remove the image.'}
        </p>
      ) : null}
    </div>
  )
}

function Row({ item, mine }: { item: FeedbackItem; mine: boolean }) {
  const Icon = KIND_ICON[item.kind]
  const [editing, setEditing] = useState(false)
  // Mirrors the API's rule: the reporter, and only while it is open. The server decides;
  // this just avoids offering a button that would be refused.
  const canEdit = mine && item.isOpen
  // The API's rule for images: the reporter while it is open, or anyone who triages.
  const canTriage = usePermission(Permissions.feedbackTriage)
  const canAttach = (mine && item.isOpen) || canTriage
  const [attaching, setAttaching] = useState(false)

  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <span title={KIND_LABEL[item.kind]} className="inline-flex text-fg-muted">
          <Icon size={16} aria-hidden />
          <span className="sr-only">{KIND_LABEL[item.kind]}</span>
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-fg">{item.title}</div>
          <div className="text-xs text-fg-muted">
            {item.reportedBy || 'unknown'} ·{' '}
            {new Date(item.createdDate).toLocaleDateString()}
            {item.page ? ` · ${item.page}` : ''}
          </div>
        </div>
        <Pill tone={SEVERITY_TONE[item.severity]}>{item.severity}</Pill>
        <Pill tone={STATUS_TONE[item.status]}>{STATUS_LABEL[item.status]}</Pill>
        {canEdit && !editing ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-fg-muted hover:text-fg"
            onClick={() => setEditing(true)}
          >
            <Pencil size={12} aria-hidden />
            Edit
          </button>
        ) : null}
        {canAttach && !attaching && item.attachments.length < 5 ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-fg-muted hover:text-fg"
            onClick={() => setAttaching(true)}
          >
            <ImagePlus size={12} aria-hidden />
            Add images
          </button>
        ) : null}
      </div>
      {editing ? <EditReport item={item} onDone={() => setEditing(false)} /> : null}
      {item.description && !editing ? (
        <p className="mt-2 whitespace-pre-wrap text-sm text-fg-muted">{item.description}</p>
      ) : null}
      <AttachmentStrip
        item={item}
        canAttach={canAttach}
        adding={attaching}
        setAdding={setAttaching}
      />
      {item.resolution && !item.isOpen ? (
        <p className="mt-2 text-sm text-fg">
          <span className="micro-label text-fg-muted">Resolution </span>
          {item.resolution}
        </p>
      ) : null}
      <Can permission={Permissions.feedbackTriage}>
        <TriageControls item={item} />
      </Can>
    </li>
  )
}

export function FeedbackPage() {
  const location = useLocation()
  const { data, isPending, error } = useFeedback()
  const { data: session } = useSession()
  const me = [session?.email, session?.displayName]
    .filter((value): value is string => Boolean(value))
    .map((value) => value.trim().toLowerCase())
  const [showClosed, setShowClosed] = useState(false)

  if (isPending) return <Loading label="Loading bugs and requests" />
  if (error) return <ErrorState error={error} />

  const items = data?.items ?? []
  const visible = showClosed ? items : items.filter((item) => item.isOpen)

  return (
    <div className="mx-auto max-w-[900px] pt-2">
      <header className="mb-6">
        <h1 className="font-heading text-3xl text-fg">Bugs &amp; requests</h1>
        <p className="mt-1 text-sm text-fg-muted">
          Anything wrong, missing, or worth doing differently. Everyone can see the queue —
          checking whether something is already filed is most of the point.
        </p>
      </header>

      <SubmitForm page={location.pathname} />

      <div className="mt-8">
        <SectionHeader
          action={
            <label className="flex items-center gap-2 text-sm text-fg-muted">
              <input
                type="checkbox"
                checked={showClosed}
                onChange={(event) => setShowClosed(event.target.checked)}
              />
              Show closed
            </label>
          }
        >
          {data?.openCount ?? 0} open
        </SectionHeader>
      </div>

      {visible.length === 0 ? (
        <EmptyState title={showClosed ? 'Nothing filed yet' : 'Nothing open'}>
          {showClosed ? 'Be the first.' : 'Everything reported has been dealt with.'}
        </EmptyState>
      ) : (
        <ul className="card divide-y divide-border">
          {visible.map((item) => (
            <Row
              key={item.id}
              item={item}
              mine={me.includes(item.reportedBy.trim().toLowerCase())}
            />
          ))}
        </ul>
      )}
    </div>
  )
}
